import { EditorState } from "draft-js";
import { Options as ExportOptions, stateToHTML } from "draft-js-export-html";
import { Options as ImportOptions, InlineCreators, stateFromHTML } from "draft-js-import-html";
import { colors } from "../../PPMXColorPicker/PPMXColorPicker";
import { LinkDecoratorType } from "./Decorators/LinkDecorator";

export const maxTabDepth = 4;

export type Richtext = {
    html?: string,
    text?: string,
}

export type RichTextValue = Richtext | string |  null | undefined;

export const isRichtext = (obj: any): boolean => typeof obj === "object" && obj !== null && "html" in obj
export const isRichOrText = (obj: any): boolean => typeof obj === "string" || isRichtext(obj);

export const isMacOS = (): boolean => navigator.platform.toUpperCase().indexOf('MAC') >= 0;

export const getMultilineText = (value?: RichTextValue): string | undefined => typeof value === "object" ? value?.text : value;

export const getMultilineHtml = (value?: RichTextValue): string | undefined => typeof value === "object" ? value?.html : value;

export const getMultilineValue = (value?: RichTextValue): string => getMultilineHtml(value) ?? getMultilineText(value) ?? '';

export const isEquialsStates = (editorState1: EditorState, editorState2: EditorState): boolean => toHtml(editorState1) === toHtml(editorState2)

type ColorMapType = { [styleName: string]: any };
export const colorStyleMap: ColorMapType = colors.reduce((map, _, i) => ({ ...map, [`color${i}`]: { color: _.color } }), {});
export const highlightStyleMap: ColorMapType = colors.reduce((map, _, i) => ({ ...map, [`highlight${i}`]: { backgroundColor: _.color } }), {});
export const customStyleMap: ColorMapType = { ...colorStyleMap, ...highlightStyleMap };

/* Export HTML */
const optionsToHtml: ExportOptions = {
    inlineStyles: Object.keys(customStyleMap).reduce((map, _) => ({ ...map, [_]: { style: customStyleMap[_] } }), {})
}

const trimEmpty = (html?: string): string | undefined => {
    if (html
        && (["<p></p>\n", "<p><br></p>"].includes(html)
            || !html?.replace(/&nbsp;/g, " ").replace(/(<([^>]+)>)/ig, "").trim())) {
        return "";
    }
    return html;
}

const toHtml = (editorState: EditorState): string | undefined => stateToHTML(editorState.getCurrentContent(), optionsToHtml);
export const toRichtext = (editorState: EditorState): Richtext => ({ html: trimEmpty(toHtml(editorState)) });

/* Import HTML */
const hexToRgb = (hex: string): string | undefined => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => (r + r + g + g + b + b));

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`
        : undefined;
}

const optionsFromHtml: ImportOptions = {
    customInlineFn: (element: any, inlineCreators: InlineCreators) => {
        if (element.tagName === "A") {
            return inlineCreators.Entity(LinkDecoratorType, { url: element.getAttribute("href") })
        }
        
        if (element.style.color) {
            const colorStyle = Object.keys(colorStyleMap).find(_ => hexToRgb(colorStyleMap[_].color) === element.style.color);
            return colorStyle ? inlineCreators.Style(colorStyle) : undefined;
        }

        if (element.style.backgroundColor) {
            const highlightStyle = Object.keys(highlightStyleMap).find(_ => hexToRgb(highlightStyleMap[_].backgroundColor) === element.style.backgroundColor);
            return highlightStyle ? inlineCreators.Style(highlightStyle) : undefined;
        }

        return undefined;
    },
}

export const fromHtml = (html: string) => stateFromHTML(html, optionsFromHtml);
